import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4Question.css';

const Homework4Question2: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [steps, setSteps] = useState<{ [key: string]: string | null }>({
    a: null,
    b: null,
    c: null,
    d: null,
    e: null,
    f: null,
    g: null,
    h: null
  });
  const [gamma, setGamma] = useState<number>(0.4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [compareSlider, setCompareSlider] = useState<number>(50);
  const [isDragging, setIsDragging] = useState(false);

  // Auto-load sample image on component mount
  useEffect(() => {
    const loadSampleImage = async () => {
      try {
        const response = await fetch('/sample-images/HW4_2.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          setOriginalImage(e.target?.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to load sample image:', err);
      }
    };
    loadSampleImage();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setCompareSlider(Math.max(0, Math.min(100, percentage)));
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setCompareSlider(Math.max(0, Math.min(100, percentage)));
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setCompareSlider(Math.max(0, Math.min(100, percentage)));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        // Clear previous results
        setSteps({
          a: null,
          b: null,
          c: null,
          d: null,
          e: null,
          f: null,
          g: null,
          h: null
        });
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const processAllSteps = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      // Process all 8 steps in parallel
      const stepNames = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
      const promises = stepNames.map(step =>
        axios.post(
          'https://process-homework4-q2-72eav6542q-uc.a.run.app',
          {
            image: base64Image,
            step: step,
            gamma: gamma
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000
          }
        )
      );

      const results = await Promise.all(promises);

      // Update all steps
      const newSteps: { [key: string]: string | null } = {};
      results.forEach((res, idx) => {
        if (res.data.success) {
          newSteps[stepNames[idx]] = `data:image/png;base64,${res.data.processed_image}`;
        }
      });

      setSteps(newSteps);

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw4-question-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/4" className="back-button">
          ← Back to Homework 4
        </Link>

        <header className="question-header">
          <div className="badge">Question 2</div>
          <h1>Bone Scan Enhancement</h1>
          <p className="subtitle">8-step enhancement procedure using edge detection and sharpening</p>
        </header>

        <div className="algorithm-info">
          <h3>Enhancement Procedure</h3>
          <p>This 8-step procedure combines multiple techniques to enhance bone scan images:</p>
          <ol>
            <li><strong>(a)</strong> Original image</li>
            <li><strong>(b)</strong> Laplacian filter applied to (a)</li>
            <li><strong>(c)</strong> First sharpening: (a) + (b)</li>
            <li><strong>(d)</strong> Sobel gradient magnitude of (a)</li>
            <li><strong>(e)</strong> 5×5 averaging filter applied to (d)</li>
            <li><strong>(f)</strong> Mask formation: (c) × (e)</li>
            <li><strong>(g)</strong> Second sharpening: (a) + (f)</li>
            <li><strong>(h)</strong> Power-law transformation applied to (g)</li>
          </ol>
        </div>

        <div className="controls-section">
          <div className="control-group">
            <label htmlFor="gamma">Gamma (for step h):</label>
            <input
              type="range"
              id="gamma"
              min="0.1"
              max="2.0"
              step="0.1"
              value={gamma}
              onChange={(e) => setGamma(Number(e.target.value))}
              className="gamma-slider"
            />
            <span className="gamma-value">{gamma.toFixed(1)}</span>
          </div>

          <div className="upload-area">
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Choose Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={processAllSteps}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Process All Steps'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="images-grid-8">
          <div className="image-panel">
            <h3>(a) Original Image</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(b) Laplacian of (a)</h3>
            <div className="image-container">
              {steps.b ? (
                <img src={steps.b} alt="Laplacian" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(c) Sharpened (a + b)</h3>
            <div className="image-container">
              {steps.c ? (
                <img src={steps.c} alt="First Sharpening" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(d) Sobel Gradient of (a)</h3>
            <div className="image-container">
              {steps.d ? (
                <img src={steps.d} alt="Sobel Gradient" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(e) Smoothed Sobel (5×5)</h3>
            <div className="image-container">
              {steps.e ? (
                <img src={steps.e} alt="Smoothed Sobel" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(f) Mask (c × e)</h3>
            <div className="image-container">
              {steps.f ? (
                <img src={steps.f} alt="Mask" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(g) Second Sharpening (a + f)</h3>
            <div className="image-container">
              {steps.g ? (
                <img src={steps.g} alt="Second Sharpening" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>(h) Power-Law Transform of (g)</h3>
            <div className="image-container">
              {steps.h ? (
                <img src={steps.h} alt="Final Result" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result here'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="info-box">
          <h4>Enhancement Analysis</h4>
          <p>
            Compare step (h) with step (a) to see the overall enhancement effect.
            Steps (b) through (g) show the intermediate processing stages.
            Adjust gamma to control the brightness of the final result.
          </p>
        </div>

        {originalImage && steps.h && (
          <div className="comparison-section">
            <h2>Before & After Comparison</h2>
            <div className="comparison-container">
              <div className="comparison-wrapper">
                <div
                  className="comparison-image-container"
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseUp}
                  onClick={handleClick}
                  onTouchMove={handleTouchMove}
                >
                  {/* Background image (h - Final Result) */}
                  <img
                    src={steps.h}
                    alt="Final Result"
                    className="comparison-img comparison-after"
                  />
                  {/* Foreground image (a - Original) with clip */}
                  <div
                    className="comparison-overlay"
                    style={{ clipPath: `inset(0 ${100 - compareSlider}% 0 0)` }}
                  >
                    <img
                      src={originalImage}
                      alt="Original"
                      className="comparison-img comparison-before"
                    />
                  </div>
                  {/* Slider line */}
                  <div
                    className="comparison-slider-line"
                    style={{ left: `${compareSlider}%` }}
                  >
                    <div className="slider-handle">
                      <span>◀</span>
                      <span>▶</span>
                    </div>
                  </div>
                </div>

                <div className="comparison-slider-control">
                  <span className="slider-label">Enhanced (h)</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={compareSlider}
                    onChange={(e) => setCompareSlider(Number(e.target.value))}
                    className="comparison-range-slider"
                  />
                  <span className="slider-label">Original (a)</span>
                </div>

                <div className="comparison-labels">
                  <div className="label-before">After Enhancement (h)</div>
                  <div className="label-after">Before Enhancement (a)</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Homework4Question2;
